import { avatarSrc } from '../../utils/employeeAvatar';
import { StudioModal as Modal, StudioDialog as ModalDialog } from '../Workspace/Workspace';
import { useContext, useEffect, useState } from 'react';
import {
  Box,
  Sheet,
  Stack,
  Typography,
  Button,
  Input,
  ModalOverflow,
  ModalClose,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  Textarea,
  Grid,
  LinearProgress,
  Avatar,
} from '@mui/joy';
import { motion } from 'framer-motion';

import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';

import { LayoutContext } from '../../context';
import { APICreateQualityGoal, APIUpdateQualityGoal, APIDeleteQualityGoal } from '../../api';

const MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

const pctHex = (p) => (p >= 80 ? '#16a34a' : p >= 50 ? '#f59e0b' : '#dc2626');
const pctColorName = (p) => (p >= 80 ? 'success' : p >= 50 ? 'warning' : 'danger');
const isPastPeriod = (y, m) => {
  const now = new Date();
  return y < now.getFullYear() || (y === now.getFullYear() && m < now.getMonth() + 1);
};
const fmtMoney = (v) => `${Number(v || 0).toLocaleString('ru-RU')} ₽`;

// ── Круговой прогресс KPI ────────────────────────────────
function CircularKPI({ percent, size = 86 }) {
  const r = (size - 10) / 2;
  const c = 2 * Math.PI * r;
  const p = Math.min(Math.max(percent || 0, 0), 100);
  const off = c - (p / 100) * c;
  return (
    <Box sx={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={8} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={pctHex(percent)}
          strokeWidth={8}
          strokeDasharray={c}
          strokeDashoffset={off}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset .6s ease' }}
        />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography level="title-lg" sx={{ fontWeight: 800, color: pctHex(percent), lineHeight: 1 }}>
          {Math.round(percent || 0)}%
        </Typography>
        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
          KPI
        </Typography>
      </Box>
    </Box>
  );
}

// ── Строка метрики ───────────────────────────────────────
function MetricRow({ name, target, actual, percent }) {
  const has = percent !== null && percent !== undefined;
  return (
    <Box sx={{ py: 0.75 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography level="body-sm" sx={{ fontWeight: 600 }}>
          {name}
        </Typography>
        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
          {actual ?? '—'} / {target}
          {has ? ` · ${Math.round(percent)}%` : ''}
        </Typography>
      </Stack>
      <LinearProgress
        determinate
        value={has ? Math.min(percent, 100) : 0}
        color={has ? pctColorName(percent) : 'neutral'}
        thickness={6}
        sx={{ borderRadius: 'sm' }}
      />
    </Box>
  );
}

// ── Форма создания/редактирования ────────────────────────
function QualityGoalForm({ goal, year, month, onSave, onCancel, loading }) {
  const [form, setForm] = useState({
    target_processed_events: goal?.target_processed_events || 0,
    target_funnel_conversion: goal?.target_funnel_conversion || 0,
    target_avg_quality_score: goal?.target_avg_quality_score || 0,
    target_deadline_compliance: goal?.target_deadline_compliance || 0,
    target_avg_cycle_time_days: goal?.target_avg_cycle_time_days || 0,
    target_rework_rate: goal?.target_rework_rate || 0,
    target_cancellation_rate: goal?.target_cancellation_rate || 0,
    fixed_salary: goal?.fixed_salary || 0,
    kpi_bonus_amount: goal?.kpi_bonus_amount || 0,
    bonus_amount: goal?.bonus_amount || 0,
    bonus_threshold_percent: goal?.bonus_threshold_percent || 80,
    comment: goal?.comment || '',
  });
  const ch = (f) => (e) => {
    const val = e?.target?.value ?? e;
    setForm((prev) => ({ ...prev, [f]: val }));
  };
  const past = !goal && isPastPeriod(year, month);

  const numField = (label, field, opts = {}, deco = null) => (
    <Grid xs={12} sm={6}>
      <FormControl>
        <FormLabel>{label}</FormLabel>
        <Input
          type="number"
          value={form[field]}
          onChange={ch(field)}
          startDecorator={deco}
          slotProps={{ input: opts }}
          sx={{ borderRadius: 'lg' }}
        />
      </FormControl>
    </Grid>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography level="title-md" sx={{ fontWeight: 700 }}>
            {goal ? 'Редактировать цель' : 'Новая цель'}
          </Typography>
          <Chip
            size="sm"
            variant="soft"
            color="primary"
            startDecorator={<CalendarMonthRoundedIcon sx={{ fontSize: 13 }} />}
          >
            {MONTHS[month - 1]} {year}
          </Chip>
        </Stack>

        {past && (
          <Sheet variant="soft" color="danger" sx={{ borderRadius: 'lg', p: 1 }}>
            <Typography level="body-xs">Этот месяц уже прошёл — выберите будущий период.</Typography>
          </Sheet>
        )}

        <Typography level="title-sm" sx={{ fontWeight: 700 }}>
          Воронка и качество
        </Typography>
        <Grid container spacing={2}>
          {numField(
            'Обработано событий (план)',
            'target_processed_events',
            { min: 0 },
            <TrendingUpRoundedIcon sx={{ fontSize: 16 }} />,
          )}
          {numField(
            'Конверсия воронки, %',
            'target_funnel_conversion',
            { min: 0, max: 100 },
            <SpeedRoundedIcon sx={{ fontSize: 16 }} />,
          )}
          {numField(
            'Средний балл качества (0–5)',
            'target_avg_quality_score',
            { step: 0.1, min: 0, max: 5 },
            <StarRoundedIcon sx={{ fontSize: 16 }} />,
          )}
          {numField(
            'Сдача в срок, %',
            'target_deadline_compliance',
            { min: 0, max: 100 },
            <VerifiedRoundedIcon sx={{ fontSize: 16 }} />,
          )}
        </Grid>

        <Divider />
        <Typography level="title-sm" sx={{ fontWeight: 700 }}>
          Лимиты (меньше — лучше)
        </Typography>
        <Grid container spacing={2}>
          {numField(
            'Среднее время цикла, дней (макс.)',
            'target_avg_cycle_time_days',
            { step: 0.1, min: 0 },
            <TimerRoundedIcon sx={{ fontSize: 16 }} />,
          )}
          {numField(
            'Доп. обмеры, % (макс.)',
            'target_rework_rate',
            { min: 0, max: 100 },
            <ReplayRoundedIcon sx={{ fontSize: 16 }} />,
          )}
          {numField(
            'Отмены/переносы, % (макс.)',
            'target_cancellation_rate',
            { min: 0, max: 100 },
            <EventBusyRoundedIcon sx={{ fontSize: 16 }} />,
          )}
        </Grid>

        <Divider />
        <Typography level="title-sm" sx={{ fontWeight: 700 }}>
          Оплата
        </Typography>
        <Grid container spacing={2}>
          {numField('Оклад, ₽', 'fixed_salary', { min: 0 }, <PaidRoundedIcon sx={{ fontSize: 16 }} />)}
          {numField(
            'Сумма за 100% KPI, ₽',
            'kpi_bonus_amount',
            { min: 0 },
            <PaidRoundedIcon sx={{ fontSize: 16 }} />,
          )}
          {numField(
            'Фикс. бонус, ₽',
            'bonus_amount',
            { min: 0 },
            <EmojiEventsRoundedIcon sx={{ fontSize: 16 }} />,
          )}
          {numField('Порог бонуса, %', 'bonus_threshold_percent', {
            min: 0,
            max: 100,
          })}
        </Grid>

        <FormControl>
          <FormLabel>Комментарий</FormLabel>
          <Textarea
            minRows={2}
            value={form.comment}
            onChange={ch('comment')}
            placeholder="Заметки к цели..."
            sx={{ borderRadius: 'lg' }}
          />
        </FormControl>

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="soft"
            color="neutral"
            size="sm"
            onClick={onCancel}
            startDecorator={<CloseRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{ borderRadius: 'lg' }}
          >
            Отмена
          </Button>
          <Button
            variant="solid"
            color="primary"
            size="sm"
            loading={loading}
            disabled={past}
            onClick={() => onSave(form)}
            startDecorator={<SaveRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{ borderRadius: 'lg' }}
          >
            Сохранить
          </Button>
        </Stack>
      </Stack>
    </motion.div>
  );
}

export default function ModalQualityGoals({
  open,
  onClose,
  master,
  initialGoal,
  year,
  month,
  onSaved,
  startInEdit = false,
  canEdit = true,
}) {
  const { tokenEmployee } = useContext(LayoutContext);
  const [goal, setGoal] = useState(initialGoal);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setGoal(initialGoal);
    setEditing(startInEdit && canEdit);
    setConfirmDelete(false);
  }, [open, initialGoal, startInEdit, canEdit]);

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      let saved;
      if (goal?.id) {
        saved = await APIUpdateQualityGoal(goal.id, formData, tokenEmployee);
      } else {
        saved = await APICreateQualityGoal(
          { ...formData, quality_master: master.id, year, month },
          tokenEmployee,
        );
      }
      setGoal(saved);
      setEditing(false);
      onSaved?.(saved || { year, month });
    } catch (e) {
      const d = e?.response?.data;
      alert(d?.non_field_errors?.[0] || d?.detail || (typeof d === 'string' ? d : 'Ошибка сохранения'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!goal?.id) return;
    setSaving(true);
    try {
      await APIDeleteQualityGoal(goal.id, tokenEmployee);
      setGoal(null);
      setConfirmDelete(false);
      onSaved?.({ year, month });
      onClose?.();
    } catch (e) {
      console.error('Delete error:', e);
    } finally {
      setSaving(false);
    }
  };

  if (!master) return null;
  const initials = ((master.name || '?').charAt(0) + (master.last_name || '').charAt(0)).toUpperCase();
  const bc = goal?.bonus_calculation || {};
  const details = bc.kpi_details || [];

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 10200 }}>
      <ModalOverflow>
        <ModalDialog
          sx={{
            width: { xs: '100%', sm: 760 },
            borderRadius: 'xl',
            p: 0,
            overflow: 'hidden',
          }}
        >
          <ModalClose />
          {/* Шапка */}
          <Box
            sx={{
              p: 2.5,
              pr: 6,
              flexShrink: 0,
              bgcolor: 'background.level1',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar src={avatarSrc(master)} variant="soft" color="primary">
                {initials}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography level="title-md" sx={{ fontWeight: 700, lineHeight: 1.1 }} noWrap>
                  {master.last_name} {master.name}
                </Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  Мастер по качеству · {MONTHS[month - 1]} {year}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ p: 2.5, minHeight: 0, overflowY: 'auto' }}>
            {editing ? (
              <QualityGoalForm
                goal={goal}
                year={year}
                month={month}
                loading={saving}
                onSave={handleSave}
                onCancel={() => (goal ? setEditing(false) : onClose())}
              />
            ) : !goal ? (
              <Stack alignItems="center" spacing={1.5} sx={{ py: 3 }}>
                <VerifiedRoundedIcon sx={{ fontSize: 44, opacity: 0.4 }} />
                <Typography level="title-sm" sx={{ color: 'text.tertiary' }}>
                  На этот месяц цели нет
                </Typography>
                {canEdit && (
                  <Button
                    color="primary"
                    variant="solid"
                    size="sm"
                    onClick={() => setEditing(true)}
                    sx={{ borderRadius: 'lg' }}
                  >
                    Создать цель
                  </Button>
                )}
              </Stack>
            ) : (
              <Stack spacing={2}>
                {/* KPI + оплата */}
                <Stack direction="row" alignItems="center" spacing={2}>
                  <CircularKPI percent={bc.kpi_percent || goal.overall_percent || 0} />
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography level="body-sm">Оклад</Typography>
                      <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                        {fmtMoney(bc.fixed_salary)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography level="body-sm">KPI-бонус</Typography>
                      <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                        {fmtMoney(bc.kpi_bonus)}
                      </Typography>
                    </Stack>
                    {bc.flat_bonus > 0 && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography level="body-sm">Фикс. бонус</Typography>
                        <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                          {fmtMoney(bc.flat_bonus)}
                        </Typography>
                      </Stack>
                    )}
                    <Divider sx={{ my: 0.5 }} />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography level="body-sm" sx={{ fontWeight: 700 }}>
                        Итого
                      </Typography>
                      <Typography level="body-sm" sx={{ fontWeight: 800, color: 'primary.600' }}>
                        {fmtMoney(bc.total_salary)}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>

                <Divider />
                <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                  Метрики KPI
                </Typography>
                {details.length === 0 && (
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    Целевые метрики не заданы.
                  </Typography>
                )}
                {details.map((d, i) => (
                  <MetricRow key={i} name={d.name} target={d.target} actual={d.actual} percent={d.percent} />
                ))}

                {goal.comment && (
                  <Sheet variant="soft" sx={{ borderRadius: 'lg', p: 1.25 }}>
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      {goal.comment}
                    </Typography>
                  </Sheet>
                )}

                {canEdit && (
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    {confirmDelete ? (
                      <>
                        <Button
                          variant="soft"
                          color="neutral"
                          size="sm"
                          onClick={() => setConfirmDelete(false)}
                          sx={{ borderRadius: 'lg' }}
                        >
                          Отмена
                        </Button>
                        <Button
                          variant="solid"
                          color="danger"
                          size="sm"
                          loading={saving}
                          onClick={handleDelete}
                          startDecorator={<DeleteRoundedIcon sx={{ fontSize: 16 }} />}
                          sx={{ borderRadius: 'lg' }}
                        >
                          Удалить
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="soft"
                          color="danger"
                          size="sm"
                          onClick={() => setConfirmDelete(true)}
                          startDecorator={<DeleteRoundedIcon sx={{ fontSize: 16 }} />}
                          sx={{ borderRadius: 'lg' }}
                        >
                          Удалить
                        </Button>
                        <Button
                          variant="solid"
                          color="primary"
                          size="sm"
                          onClick={() => setEditing(true)}
                          startDecorator={<EditRoundedIcon sx={{ fontSize: 16 }} />}
                          sx={{ borderRadius: 'lg' }}
                        >
                          Редактировать
                        </Button>
                      </>
                    )}
                  </Stack>
                )}
              </Stack>
            )}
          </Box>
        </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
}
